// Global
import { GetComponentServerProps, Text } from '@sitecore-content-sdk/nextjs';
import React, { JSX } from 'react';
import { defaultConfig, tv } from 'tailwind-variants';

// Local
import BreadcrumbQuery, {
  BreadcrumbQueryResult,
} from 'components/authorable/shared/site-structure/Breadcrumb/Breadcrumb.graphql';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { ComponentProps } from 'lib/component-props';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import classNames from 'classnames';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useRouter } from 'next/router';
import client from 'lib/sitecore-client';
import { isServerSidePropsContext } from '@sitecore-content-sdk/nextjs/utils';

const DISABLED_LINK_FLAG = 'breadcrumb';
defaultConfig.twMerge = false;

export type BreadcrumbDataType = ComponentProps & BreadcrumbQueryResult;

export const Default = (props: BreadcrumbDataType): JSX.Element => {
  const { ancestors, Title } = props?.currentPage || {};
  const { componentName, dataSource } = props?.rendering || {};
  const { currentScreenWidth } = useCurrentScreenType();
  const isEditing = useIsEditing();

  const filteredAncestors = ancestors
    ?.slice()
    .reverse()
    .filter(
      // Filter out any ancestors have have either no page url or title.
      (ancestor) => ancestor?.url?.path.length && ancestor?.Title?.jsonValue?.value.length
    )
    .filter(
      // Filter out any ancestors that are flagged as disabled for the breadcrumb.
      (ancestor) =>
        !ancestor?.navigationFilter?.targetItems.length ||
        ancestor?.navigationFilter?.targetItems.find(
          (name) => name?.key?.jsonValue?.value !== DISABLED_LINK_FLAG
        )
    );

  // Implemented keyboard navigation Spacebar activate link/button
  React.useEffect(() => {
    const ul = document.querySelector('ul[id="breadcrumbs-ul"]');
    if (!ul) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && document.activeElement?.tagName === 'A') {
        e.preventDefault();
        const event = new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true });
        e.target?.dispatchEvent(event);
      }
    };
    ul.addEventListener('keydown', handleKeyDown);
    return () => ul.removeEventListener('keydown', handleKeyDown);
  }, []);
  const router = useRouter();
  /*
   * Rendering
   */

  const { base, currentPage, icon, iconContainer, linkWrapper, list, listItem } =
    TAILWIND_VARIANTS();

  return (
    <>
      {router.query.private !== 'true' && filteredAncestors && filteredAncestors.length > 0 ? (
        <nav
          aria-label="Breadcrumb"
          className={base()}
          data-component="authorable/shared/site-structure/breadcrumb"
          data-testid="breadcrumbs"
        >
          <ul className={list()} id="breadcrumbs-ul">
            {currentScreenWidth >= 1024 &&
              filteredAncestors.map((ancestor, index) => {
                const { url, Title } = ancestor || {};

                return (
                  <li className={listItem()} key={url?.path}>
                    <LinkWrapper
                      className={linkWrapper()}
                      field={{
                        value: { href: url?.path },
                      }}
                      gtmEvent={{
                        event: 'link',
                        type: 'breadcrumb',
                        'gtm.element.dataset.gtmDatasourceId': dataSource,
                        'gtm.element.dataset.gtmComponentName': componentName,
                      }}
                    >
                      <Text
                        encode={false}
                        field={{
                          value: Title?.jsonValue?.value,
                        }}
                        tag="abbr"
                        title={Title?.jsonValue?.value}
                        className={classNames(
                          'no-underline',
                          index > 1 &&
                            'overflow-hidden whitespace-nowrap text-ellipsis max-w-[26ch]'
                        )}
                      />
                    </LinkWrapper>
                    <span className={iconContainer()}>
                      <SvgIcon
                        className={icon()}
                        fill="none"
                        icon="chevron-right"
                        size="md"
                        viewBox="0 0 24 24"
                      />
                    </span>
                  </li>
                );
              })}
            {currentScreenWidth >= 1024 && ancestors?.length > 0 && (
              <li aria-current="true" className={currentPage()}>
                <Text
                  encode={false}
                  field={{
                    value: Title?.jsonValue?.value,
                  }}
                  tag="abbr"
                  title={Title?.jsonValue?.value}
                  className="no-underline overflow-hidden whitespace-nowrap text-ellipsis max-w-[26ch]"
                />
              </li>
            )}
            {currentScreenWidth < 1024 && (
              <li
                className={listItem()}
                key={filteredAncestors[filteredAncestors.length - 1]?.url?.path}
              >
                <LinkWrapper
                  className={classNames(
                    linkWrapper(),
                    'gap-5',
                    isEditing && '[&_a]:flex [&_a]:gap-5 [&_a]:items-center'
                  )}
                  field={{
                    value: {
                      href: filteredAncestors[filteredAncestors.length - 1]?.url?.path,
                    },
                  }}
                  gtmEvent={{
                    event: 'link',
                    type: 'breadcrumb',
                    'gtm.element.dataset.gtmDatasourceId': dataSource,
                    'gtm.element.dataset.gtmComponentName': componentName,
                  }}
                >
                  <span className={iconContainer()}>
                    <SvgIcon
                      className={'text-blue-base hover:text-darkblue-base rotate-180'}
                      fill="none"
                      icon="chevron-right"
                      size="sm"
                      viewBox="0 0 24 24"
                    />
                  </span>
                  <span>
                    {filteredAncestors[filteredAncestors.length - 1]?.Title?.jsonValue?.value}
                  </span>
                </LinkWrapper>
              </li>
            )}
          </ul>
        </nav>
      ) : (
        <></>
      )}
    </>
  );
};

export const getComponentServerProps: GetComponentServerProps = async (
  rendering,
  layoutData,
  context
) => {
  if (!isServerSidePropsContext(context)) {
    try {
      const result = await client.getData<BreadcrumbQueryResult>(BreadcrumbQuery, {
        datasource: rendering.dataSource,
        itemID: layoutData?.sitecore?.route?.itemId,
        language: layoutData?.sitecore?.context?.language,
        params: rendering.params,
      });
      return result;
    } catch (e) {
      console.error(e);
      return {};
    }
  }
  return {};
};

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['component', 'bg-white'],
    currentPage: [
      'flex',
      'items-center',
      'font-bold',
      'list-none',
      'text-gray-500',
      'md:my-0',
      'md:p-0',
      'font-centuryGothic',
      'text-14',
      'leading-48',
    ],
    icon: ['h-auto', 'w-auto'],
    iconContainer: ['lg:ml-5 mt-1 lg:mt-0'],
    linkWrapper: [
      'flex',
      'font-bold',
      'items-center',
      'font-centuryGothic',
      'text-blue-base',
      'hover:underline',
      'hover:underline-offset-4',
      'hover:text-blue-base lg:hover:text-darkblue-base',
      '!min-h-24',
      'text-14',
      'lg:leading-48',
    ],
    list: [
      'flex',
      'flex-wrap',
      'gap-x-5',
      'gap-x-2',
      'items-center',
      'list',
      'm-auto w-full max-w-screen-xxl py-2',
      'px-10 lg:px-20',
      'print:hidden',
    ],
    listItem: ['flex', 'items-center', 'list-none'],
  },
});
